import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, PanelLeftClose, PanelLeftOpen, Menu, X, LogOut, ChevronRight, ChevronDown,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { visibleGroups, findNavItem, pathFor } from './navConfig';
import CommandPalette from './CommandPalette';

const COLLAPSE_KEY = 'zocs_sidebar_collapsed';

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
export const MOD_KEY = isMac ? '⌘' : 'Ctrl';

function initials(name = '') {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';
}

function Sidebar({ groups, currentPage, onNavigate, collapsed, onToggle, mobile, onClose }) {
  return (
    <aside className={`flex flex-col h-full bg-white border-r border-gray-200 transition-[width] duration-150
      ${collapsed && !mobile ? 'w-[60px]' : 'w-60'}`}>
      <div className="h-14 flex items-center justify-between px-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center shrink-0">Z</div>
          {(!collapsed || mobile) && <span className="font-semibold text-gray-900 tracking-tight">ZOCS</span>}
        </div>
        {mobile ? (
          <button onClick={onClose} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100" aria-label="Close menu"><X size={16} /></button>
        ) : !collapsed && (
          <button onClick={onToggle} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100" aria-label="Collapse sidebar" title="Collapse sidebar">
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {groups.map(g => (
          <div key={g.label} className="mb-4">
            {(!collapsed || mobile)
              ? <div className="px-2 mb-1 text-[11px] font-medium uppercase tracking-wider text-gray-400">{g.label}</div>
              : <div className="mx-2 mb-2 border-t border-gray-100" />}
            {g.items.map(item => {
              const Icon = item.icon;
              const active = currentPage === item.key;
              return (
                <button key={item.key} onClick={() => onNavigate(item.key)}
                  title={collapsed && !mobile ? item.label : undefined}
                  className={`w-full flex items-center gap-2.5 rounded-md text-[13px] mb-0.5 transition-colors
                    ${collapsed && !mobile ? 'justify-center h-9' : 'px-2 h-8'}
                    ${active ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                  <Icon size={16} strokeWidth={active ? 2.2 : 1.8} className={active ? 'text-zoddle-green' : 'text-gray-400'} />
                  {(!collapsed || mobile) && <span className="truncate">{item.label}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {collapsed && !mobile && (
        <button onClick={onToggle} className="h-11 flex items-center justify-center border-t border-gray-200 text-gray-400 hover:text-gray-700" aria-label="Expand sidebar" title="Expand sidebar">
          <PanelLeftOpen size={16} />
        </button>
      )}
    </aside>
  );
}

function UserMenu({ auth, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 rounded-md pl-1 pr-1.5 py-1 hover:bg-gray-100">
        <span className="w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-[11px] font-semibold flex items-center justify-center">{initials(auth.full_name)}</span>
        <span className="hidden md:block text-[13px] text-gray-700">{auth.full_name}</span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-40">
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="text-[13px] font-medium text-gray-900 truncate">{auth.full_name}</div>
            <div className="text-xs text-gray-500">{auth.access_level?.replace('_', ' ')}</div>
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 hover:text-zoddle-pink">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { auth, logout } = useAuth();
  const { currentPage, navigate, setCurrentPage } = useNavigation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const groups = visibleGroups(auth);
  const here = findNavItem(currentPage);

  // A URL for a page this role can't open (or an unknown one) falls back to
  // the first page they can see, instead of a blank screen.
  const allowed = groups.some(g => g.items.some(i => i.key === currentPage));
  useEffect(() => {
    if (!allowed && groups[0]) {
      const first = groups[0].items[0].key;
      setCurrentPage(first);
      window.history.replaceState(null, '', pathFor(first));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  function toggleCollapsed() {
    setCollapsed(c => {
      try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* storage blocked: just don't remember */ }
      return !c;
    });
  }

  const notify = useCallback((message, kind = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ message, kind });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // ⌘K / Ctrl+K opens the palette from anywhere, even while typing in a field.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function go(page, data) {
    navigate(page, data);
    setMobileOpen(false);
  }

  return (
    <div className="h-screen flex bg-gray-50 text-gray-900 antialiased">
      <div className="hidden md:block shrink-0">
        <Sidebar groups={groups} currentPage={currentPage} onNavigate={go}
          collapsed={collapsed} onToggle={toggleCollapsed} />
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="relative h-full shadow-xl">
            <Sidebar groups={groups} currentPage={currentPage} onNavigate={go} mobile onClose={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 bg-white border-b border-gray-200 flex items-center gap-3 px-3 md:px-5">
          <button onClick={() => setMobileOpen(true)} className="md:hidden p-1.5 rounded-md text-gray-500 hover:bg-gray-100" aria-label="Open menu">
            <Menu size={18} />
          </button>
          <div className="flex items-center gap-1.5 text-[13px] min-w-0">
            {here && <span className="hidden sm:inline text-gray-400">{here.group.label}</span>}
            {here && <ChevronRight size={14} className="hidden sm:inline text-gray-300" />}
            <span className="font-medium text-gray-900 truncate">{here?.item.label || 'ZOCS'}</span>
          </div>

          <div className="flex-1" />

          <button onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 h-8 w-9 sm:w-64 justify-center sm:justify-start px-2.5 rounded-md border border-gray-200 bg-gray-50 text-[13px] text-gray-400 hover:border-gray-300 hover:text-gray-600 transition-colors">
            <Search size={14} />
            <span className="hidden sm:inline flex-1 text-left">Search or jump to…</span>
            <kbd className="hidden sm:inline text-[11px] font-sans text-gray-400 border border-gray-200 bg-white rounded px-1.5 py-0.5">{MOD_KEY} K</kbd>
          </button>

          <UserMenu auth={auth} onLogout={logout} />
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onNavigate={go} notify={notify} />

      {toast && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-gray-900 text-white text-[13px] rounded-lg shadow-lg px-4 py-3 max-w-sm">
          {toast.kind === 'error'
            ? <AlertCircle size={16} className="text-zoddle-pink shrink-0" />
            : <CheckCircle2 size={16} className="text-zoddle-green shrink-0" />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
